import React, { useState, useMemo } from 'react';
import { 
  X, 
  FileText, 
  Printer, 
  Calendar, 
  FileSpreadsheet,
  Trash2
} from 'lucide-react';
import { ManipulatedVariable, StepTest, Disturbance, ProjectSummaryStats } from '../types';
import { calculateReadinessScore, calculateDirectionalBalance } from '../services/readinessCalculator';
import { ExportService } from '../services/exportService';

interface ReportsModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectName?: string;
  campaignName?: string;
  mvs: ManipulatedVariable[];
  steps: StepTest[];
  disturbances: Disturbance[];
  stats: ProjectSummaryStats;
  onDeleteStep?: (stepId: string) => Promise<void> | void;
  targetMovesPerMv?: number;
}

type ReportTab = 'all' | 'summary' | 'steps' | 'readiness' | 'disturbances';

export const ReportsModal: React.FC<ReportsModalProps> = ({
  isOpen,
  onClose,
  projectName,
  campaignName,
  mvs,
  steps,
  disturbances,
  stats,
  onDeleteStep,
  targetMovesPerMv = 20,
}) => {
  const [activeTab, setActiveTab] = useState<ReportTab>('all');
  const [selectedDate, setSelectedDate] = useState<string>('');

  const activeProjectName = projectName || campaignName || stats.projectName || stats.campaignName || 'APC Step Test Project';

  const reportStatus = useMemo(() => {
    const progress = stats.projectProgressPct ?? stats.campaignProgressPct ?? 0;
    if (progress >= 100) return 'Step Testing Complete (Model Ready)';
    if (stats.activeTestsCount > 0) return 'Step Testing In-Progress (Active Steps)';
    if (stats.totalValidMoves > 0) return 'Step Testing In-Progress';
    return 'Step Testing Planned';
  }, [stats]);

  const displayedSteps = useMemo(() => {
    if (!selectedDate) return steps;
    return steps.filter((s) => s.timestamp.startsWith(selectedDate));
  }, [steps, selectedDate]);

  if (!isOpen) return null;

  const handleExportExcel = () => {
    ExportService.exportExcelWorkbook(activeProjectName, mvs, steps, disturbances, stats, targetMovesPerMv);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/40 backdrop-blur-xs overflow-y-auto print-report-modal">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-xl w-full max-w-5xl overflow-hidden my-4 sm:my-8 print-report-card">
        {/* Header (Hidden in Print) */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 no-print">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-600" />
            <h2 className="font-semibold text-slate-900 text-base">Project Reports & Exports</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportExcel}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 shadow-xs transition-colors cursor-pointer"
              title="Download all reports on separate worksheets in one Excel workbook (.xlsx)"
            >
              <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
              <span>Excel (.xlsx) - All Reports</span>
            </button>

            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold bg-sky-600 hover:bg-sky-700 text-white shadow-xs transition-colors cursor-pointer"
              title="Print or Save all reports as PDF (separate pages per report)"
            >
              <Printer className="w-4 h-4" />
              <span>Print / PDF - All Reports</span>
            </button>

            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tab Controls (Hidden in Print) */}
        <div className="flex items-center gap-1.5 px-5 py-2.5 bg-slate-50 border-b border-slate-200 no-print overflow-x-auto scrollbar-none">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
              activeTab === 'all'
                ? 'bg-white text-slate-900 shadow-xs border border-slate-200'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            All Reports (Complete Dossier)
          </button>
          <button
            onClick={() => setActiveTab('summary')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
              activeTab === 'summary'
                ? 'bg-white text-slate-900 shadow-xs border border-slate-200'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            1. Project Summary
          </button>
          <button
            onClick={() => setActiveTab('steps')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
              activeTab === 'steps'
                ? 'bg-white text-slate-900 shadow-xs border border-slate-200'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            2. Step Tests Log ({steps.length})
          </button>
          <button
            onClick={() => setActiveTab('readiness')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
              activeTab === 'readiness'
                ? 'bg-white text-slate-900 shadow-xs border border-slate-200'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            3. Model Readiness Certification
          </button>
          <button
            onClick={() => setActiveTab('disturbances')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
              activeTab === 'disturbances'
                ? 'bg-white text-slate-900 shadow-xs border border-slate-200'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            4. Plant Disturbances ({disturbances.length})
          </button>
        </div>

        {/* Report Scroll Area */}
        <div className="p-5 sm:p-6 max-h-[75vh] overflow-y-auto bg-white text-slate-800 font-sans print:p-0 print-report-content">

          {/* ========================================================================= */}
          {/* REPORT 1: PROJECT EXECUTIVE SUMMARY & SUBCONTROLLER BREAKDOWN */}
          {/* ========================================================================= */}
          <div className={`space-y-4 ${activeTab === 'all' || activeTab === 'summary' ? 'block' : 'hidden print:block'}`}>
            <div className="border-b border-slate-200 pb-3 mb-4 flex justify-between items-start print:border-b-2 print:border-slate-800 print:pb-2 print:mb-3">
              <div>
                <div className="text-[11px] font-mono text-sky-700 uppercase tracking-wider font-bold">
                  Report 1 of 4: Project Executive Summary & Subcontroller Breakdown
                </div>
                <h1 className="text-base sm:text-lg font-bold text-slate-900 print:text-xl print:text-slate-950">
                  {activeProjectName}
                </h1>
                <p className="text-xs text-slate-500">
                  Keyera Advanced Process Control &bull; APC Step-Testing Project Report
                </p>
              </div>
              <div className="text-right text-xs font-mono text-slate-600 print:text-[8pt]">
                <div>Generated: {new Date().toLocaleDateString()} {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                <div className="font-semibold text-sky-700">Status: {reportStatus}</div>
              </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 print:grid-cols-4 print:gap-2 print:mb-3">
              <div className="bg-slate-50 p-3.5 border border-slate-200 rounded-xl print:p-2 print:rounded-none">
                <div className="text-xs text-slate-500 print:text-[7.5pt]">Valid Moves</div>
                <div className="text-xl font-bold font-mono text-slate-900 print:text-sm">
                  {stats.totalValidMoves} / {stats.targetMovesTotal}
                </div>
              </div>
              <div className="bg-slate-50 p-3.5 border border-slate-200 rounded-xl print:p-2 print:rounded-none">
                <div className="text-xs text-slate-500 print:text-[7.5pt]">Progress</div>
                <div className="text-xl font-bold font-mono text-sky-700 print:text-sm">
                  {stats.projectProgressPct ?? stats.campaignProgressPct}%
                </div>
              </div>
              <div className="bg-slate-50 p-3.5 border border-slate-200 rounded-xl print:p-2 print:rounded-none">
                <div className="text-xs text-slate-500 print:text-[7.5pt]">Remaining Moves</div>
                <div className="text-xl font-bold font-mono text-amber-700 print:text-sm">
                  {stats.remainingMoves}
                </div>
              </div>
              <div className="bg-slate-50 p-3.5 border border-slate-200 rounded-xl print:p-2 print:rounded-none">
                <div className="text-xs text-slate-500 print:text-[7.5pt]">Rejected Moves</div>
                <div className="text-xl font-bold font-mono text-rose-600 print:text-sm">
                  {stats.rejectedTestsCount}
                </div>
              </div>
            </div>

            {/* Subcontroller Breakdown Table */}
            <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-xs print-table-wrapper">
              <table className="w-full text-left text-xs border-collapse print-table">
                <thead>
                  <tr className="bg-slate-50 text-slate-600 border-b border-slate-200 font-mono uppercase text-[11px]">
                    <th className="py-2.5 px-3">Subcontroller</th>
                    <th className="py-2.5 px-3 text-center">MVs</th>
                    <th className="py-2.5 px-3 text-center">Valid Moves</th>
                    <th className="py-2.5 px-3 text-center">Target</th>
                    <th className="py-2.5 px-3 text-center">Readiness %</th>
                    <th className="py-2.5 px-3 text-center text-emerald-700">Ready ({targetMovesPerMv}+)</th>
                    <th className="py-2.5 px-3 text-center text-amber-700">Adequate ({Math.ceil(targetMovesPerMv * 0.5)}-{targetMovesPerMv - 1})</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-mono">
                  {Object.keys(stats.controllerStats || {}).length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-4 text-center text-slate-400 font-sans">
                        No subcontrollers defined yet.
                      </td>
                    </tr>
                  ) : (
                    Object.keys(stats.controllerStats || {}).map((ctrl) => {
                      const data = stats.controllerStats[ctrl];
                      if (!data) return null;
                      return (
                        <tr key={ctrl}>
                          <td className="py-2.5 px-3 font-sans font-semibold text-slate-900">{ctrl}</td>
                          <td className="py-2.5 px-3 text-center text-slate-600">{data.totalMvs}</td>
                          <td className="py-2.5 px-3 text-center font-bold text-sky-700">{data.validMoves}</td>
                          <td className="py-2.5 px-3 text-center text-slate-500">{data.targetMoves}</td>
                          <td className="py-2.5 px-3 text-center font-semibold text-slate-900">{data.readinessPct}%</td>
                          <td className="py-2.5 px-3 text-center text-emerald-700 font-bold">{data.mvsReadyCount}</td>
                          <td className="py-2.5 px-3 text-center text-amber-700 font-bold">{data.mvsAdequateCount}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ========================================================================= */}
          {/* REPORT 2: STEP TESTS EXECUTION LOG */}
          {/* ========================================================================= */}
          <div className={`space-y-4 print-page-break ${activeTab === 'all' || activeTab === 'steps' ? 'block mt-10 print:mt-0' : 'hidden print:block'}`}>
            <div className="border-b border-slate-200 pb-3 mb-4 flex justify-between items-start print:border-b-2 print:border-slate-800 print:pb-2 print:mb-3">
              <div>
                <div className="text-[11px] font-mono text-sky-700 uppercase tracking-wider font-bold">
                  Report 2 of 4: Step Tests Execution Log
                </div>
                <h1 className="text-base sm:text-lg font-bold text-slate-900 print:text-xl print:text-slate-950">
                  {activeProjectName}
                </h1>
                <p className="text-xs text-slate-500">
                  Chronological Step-Testing Execution & Evaluation Records ({steps.length} total tests)
                </p>
              </div>
              <div className="text-right text-xs font-mono text-slate-600 print:text-[8pt]">
                <div>Total Records: {steps.length}</div>
                <div className="font-semibold text-sky-700">Status: {reportStatus}</div>
                <div>Generated: {new Date().toLocaleDateString()} {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
              </div>
            </div>

            {/* Date filter on screen */}
            <div className="flex items-center justify-between no-print mb-2 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-500" />
                <span className="text-xs font-medium text-slate-700">Filter Date:</span>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="bg-slate-50 text-slate-800 text-xs font-mono border border-slate-300 rounded-lg px-2.5 py-1"
                />
                {selectedDate && (
                  <button
                    onClick={() => setSelectedDate('')}
                    className="text-xs text-sky-600 hover:text-sky-800 font-semibold cursor-pointer underline"
                  >
                    Show All Dates ({steps.length})
                  </button>
                )}
              </div>
              <span className="text-xs text-slate-500">
                {displayedSteps.length} of {steps.length} tests displayed
              </span>
            </div>

            {/* Step Tests Table */}
            <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-xs print-table-wrapper">
              <table className="w-full text-left text-xs border-collapse print-table">
                <thead>
                  <tr className="bg-slate-50 text-slate-600 border-b border-slate-200 font-mono uppercase text-[11px]">
                    <th className="py-2.5 px-3">Date / Time</th>
                    <th className="py-2.5 px-3">MV Tag</th>
                    <th className="py-2.5 px-2 text-center">Dir</th>
                    <th className="py-2.5 px-2 text-right">Step</th>
                    <th className="py-2.5 px-2 text-right">Baseline</th>
                    <th className="py-2.5 px-2 text-right">Target</th>
                    <th className="py-2.5 px-2 text-center">Hold (min)</th>
                    <th className="py-2.5 px-2 text-center">Result</th>
                    <th className="py-2.5 px-3">Personnel</th>
                    <th className="py-2.5 px-3 min-w-[140px]">Comments</th>
                    {onDeleteStep && <th className="py-2.5 px-3 text-right no-print">Action</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-sans">
                  {/* In print, print all steps so full report is exported; on screen show displayedSteps */}
                  {(activeTab === 'steps' && selectedDate ? displayedSteps : steps).length === 0 ? (
                    <tr>
                      <td colSpan={onDeleteStep ? 11 : 10} className="py-8 text-center text-slate-400">
                        No step tests recorded yet.
                      </td>
                    </tr>
                  ) : (
                    (activeTab === 'steps' && selectedDate ? displayedSteps : steps).map((s) => (
                      <tr key={s.id} className="hover:bg-slate-50">
                        <td className="py-2 px-3 font-mono text-slate-600 whitespace-nowrap">
                          {s.timestamp.replace('T', ' ').slice(0, 16)}
                        </td>
                        <td className="py-2 px-3 font-mono font-bold text-sky-700">{s.mvTag}</td>
                        <td className="py-2 px-2 text-center font-mono font-bold">
                          <span className={s.direction === '+' ? 'text-emerald-700' : 'text-rose-600'}>
                            {s.direction}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-right font-mono">{s.stepSize}</td>
                        <td className="py-2 px-2 text-right font-mono">{s.baselineSp}</td>
                        <td className="py-2 px-2 text-right font-mono">{s.targetSp}</td>
                        <td className="py-2 px-2 text-center font-mono">{s.holdDuration}</td>
                        <td className="py-2 px-2 text-center font-mono font-bold">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            s.status === 'PASS' 
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                              : s.status === 'IN_PROGRESS'
                              ? 'bg-sky-50 text-sky-700 border border-sky-200'
                              : 'bg-rose-50 text-rose-700 border border-rose-200'
                          }`}>
                            {s.status}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-slate-600 text-[11px] whitespace-nowrap">
                          {s.engineer} / {s.operator}
                        </td>
                        <td className="py-2 px-3 text-slate-700 text-[11px] max-w-xs break-words">
                          {s.comments && s.comments.trim() !== '' ? s.comments : '—'}
                        </td>
                        {onDeleteStep && (
                          <td className="py-2 px-3 text-right no-print">
                            <button
                              type="button"
                              onClick={() => {
                                if (window.confirm(`Are you sure you want to permanently delete step test for ${s.mvTag} (${s.timestamp})?`)) {
                                  onDeleteStep(s.id);
                                }
                              }}
                              className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors cursor-pointer"
                              title="Delete step test"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ========================================================================= */}
          {/* REPORT 3: MODEL READINESS CERTIFICATION */}
          {/* ========================================================================= */}
          <div className={`space-y-4 print-page-break ${activeTab === 'all' || activeTab === 'readiness' ? 'block mt-10 print:mt-0' : 'hidden print:block'}`}>
            <div className="border-b border-slate-200 pb-3 mb-4 flex justify-between items-start print:border-b-2 print:border-slate-800 print:pb-2 print:mb-3">
              <div>
                <div className="text-[11px] font-mono text-sky-700 uppercase tracking-wider font-bold">
                  Report 3 of 4: Model Identification Readiness Certification
                </div>
                <h1 className="text-base sm:text-lg font-bold text-slate-900 print:text-xl print:text-slate-950">
                  {activeProjectName}
                </h1>
                <p className="text-xs text-slate-500">
                  Manipulated Variable Readiness & Directional Balance Certification
                </p>
              </div>
              <div className="text-right text-xs font-mono text-slate-600 print:text-[8pt]">
                <div>Variables: {mvs.length}</div>
                <div className="font-semibold text-sky-700">Status: {reportStatus}</div>
                <div>Generated: {new Date().toLocaleDateString()} {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
              </div>
            </div>

            {/* MV Matrix Table */}
            <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-xs print-table-wrapper">
              <table className="w-full text-left text-xs border-collapse print-table">
                <thead>
                  <tr className="bg-slate-50 text-slate-600 border-b border-slate-200 font-mono uppercase text-[11px]">
                    <th className="py-2.5 px-3">Tag</th>
                    <th className="py-2.5 px-3">Description</th>
                    <th className="py-2.5 px-3">Subcontroller</th>
                    <th className="py-2.5 px-2 text-center">Pos (+)</th>
                    <th className="py-2.5 px-2 text-center">Neg (-)</th>
                    <th className="py-2.5 px-2 text-center font-bold">Valid Moves</th>
                    <th className="py-2.5 px-2 text-center">Balance</th>
                    <th className="py-2.5 px-3 text-center">Readiness Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-sans">
                  {mvs.map((m) => {
                    const mvTarget = m.targetMoves || targetMovesPerMv;
                    const readiness = calculateReadinessScore(m.validMoves, mvTarget);
                    const balance = calculateDirectionalBalance(m.positiveMoves, m.negativeMoves);
                    return (
                      <tr key={m.id}>
                        <td className="py-2 px-3 font-mono font-bold text-sky-700">{m.tag}</td>
                        <td className="py-2 px-3 text-slate-800">{m.description}</td>
                        <td className="py-2 px-3 text-slate-500 font-mono text-[11px]">{m.controller}</td>
                        <td className="py-2 px-2 text-center font-mono text-emerald-700">+{m.positiveMoves}</td>
                        <td className="py-2 px-2 text-center font-mono text-rose-600">-{m.negativeMoves}</td>
                        <td className="py-2 px-2 text-center font-mono font-bold text-slate-900">{m.validMoves}</td>
                        <td className="py-2 px-2 text-center font-mono text-[11px] text-slate-500">{balance.skew}</td>
                        <td className="py-2 px-3 text-center font-mono font-semibold">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] ${
                              readiness === 'Ready for Modeling'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : readiness === 'Adequate'
                                ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                : 'bg-slate-100 text-slate-600 border border-slate-200'
                            }`}
                          >
                            {readiness}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Sign-off Block */}
            <div className="mt-8 pt-4 border-t border-slate-200 grid grid-cols-2 gap-8 text-xs font-mono print-signoff print-avoid-break">
              <div>
                <div className="border-b border-slate-300 h-8 mb-1"></div>
                <div className="text-slate-500">Lead APC Engineer Signature</div>
              </div>
              <div>
                <div className="border-b border-slate-300 h-8 mb-1"></div>
                <div className="text-slate-500">Operations Superintendent Sign-off</div>
              </div>
            </div>
          </div>

          {/* ========================================================================= */}
          {/* REPORT 4: PLANT DISTURBANCES LOG */}
          {/* ========================================================================= */}
          <div className={`space-y-4 print-page-break ${activeTab === 'all' || activeTab === 'disturbances' ? 'block mt-10 print:mt-0' : 'hidden print:block'}`}>
            <div className="border-b border-slate-200 pb-3 mb-4 flex justify-between items-start print:border-b-2 print:border-slate-800 print:pb-2 print:mb-3">
              <div>
                <div className="text-[11px] font-mono text-sky-700 uppercase tracking-wider font-bold">
                  Report 4 of 4: Plant Disturbances Log
                </div>
                <h1 className="text-base sm:text-lg font-bold text-slate-900 print:text-xl print:text-slate-950">
                  {activeProjectName}
                </h1>
                <p className="text-xs text-slate-500">
                  Chronological Plant Upsets, Maintenance & Operational Disturbances ({disturbances.length} total records)
                </p>
              </div>
              <div className="text-right text-xs font-mono text-slate-600 print:text-[8pt]">
                <div>Disturbances: {disturbances.length}</div>
                <div className="font-semibold text-sky-700">Status: {reportStatus}</div>
                <div>Generated: {new Date().toLocaleDateString()} {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
              </div>
            </div>

            {/* Disturbances Table */}
            <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-xs print-table-wrapper">
              <table className="w-full text-left text-xs border-collapse print-table">
                <thead>
                  <tr className="bg-slate-50 text-slate-600 border-b border-slate-200 font-mono uppercase text-[11px]">
                    <th className="py-2.5 px-3">Date / Time</th>
                    <th className="py-2.5 px-3">Category</th>
                    <th className="py-2.5 px-3">Subcontroller</th>
                    <th className="py-2.5 px-3">Description</th>
                    <th className="py-2.5 px-3">Engineer</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-sans">
                  {disturbances.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-slate-400">
                        No plant disturbances recorded yet.
                      </td>
                    </tr>
                  ) : (
                    disturbances.map((d) => (
                      <tr key={d.id} className="hover:bg-slate-50">
                        <td className="py-2 px-3 font-mono text-slate-600 whitespace-nowrap">
                          {d.timestamp.replace('T', ' ').slice(0, 16)}
                        </td>
                        <td className="py-2 px-3 font-mono font-bold text-amber-700 whitespace-nowrap">
                          {d.category}
                        </td>
                        <td className="py-2 px-3 font-mono text-slate-600 whitespace-nowrap">
                          {d.controller || 'Plant-Wide'}
                        </td>
                        <td className="py-2 px-3 text-slate-800 break-words">
                          {d.description}
                        </td>
                        <td className="py-2 px-3 text-slate-600 text-[11px] whitespace-nowrap">
                          {d.engineer}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};
